import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon } from 'lucide-react';
import { MetaPixel } from './components/MetaPixel';
import { SoldPopup } from './components/SoldPopup';
import { CTAButton } from './components/CTAButton';
import { Accordion } from './components/Accordion';
import { BonusCarousel } from './components/BonusCarousel';
import { Reviews } from './components/Reviews';
import { Testimonials } from './components/Testimonials';
import { ComparisonTable } from './components/ComparisonTable';
import { PurchaseSection } from './components/PurchaseSection';
import { ObjectionsSupport } from './components/ObjectionsSupport';
import { goToPurchase } from './lib/checkout';
import { PRODUCT_IMAGE, PROBLEMS, MODULES, MODULE_GROUPS } from './lib/content';
const fadeUp = {
  initial: {
    opacity: 0,
    y: 24
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-60px'
  },
  transition: {
    duration: 0.5
  }
};
export function App() {
  return <div className="court-bg relative min-h-screen w-full" data-id="element-0">
      <MetaPixel data-id="element-1" />
      <SoldPopup data-id="element-2" />

      {/* Blur + dark overlay over the court background */}
      <div className="fixed inset-0 z-0 bg-black/75 backdrop-blur-md" aria-hidden data-id="element-3" />

      {/* Mobile-first single column */}
      <main className="relative z-10 mx-auto w-full max-w-md px-4 pb-16 pt-8" data-id="element-4">
        {/* ===== HERO ===== */}
        <motion.header {...fadeUp} className="text-center" data-id="element-5">
          <h1 className="font-display text-3xl uppercase leading-tight text-white" data-id="element-6">
            Everything you need to become the coach your players deserve.
          </h1>
        </motion.header>

        <motion.button {...fadeUp} onClick={goToPurchase} className="mt-6 block w-full" aria-label="Go to purchase" data-id="element-7">
          <img src={PRODUCT_IMAGE} alt="Complete Basketball Kit — player health, performance, and mental game system with 6 volumes and 5 bonuses" className="w-full rounded-2xl border border-orange-500/30 shadow-2xl" data-id="element-8" />
        </motion.button>

        <motion.p {...fadeUp} className="mt-5 text-center text-base font-medium text-neutral-200" data-id="element-9">
          Drills, planners, stats sheets and coaching guides — all in one place.
        </motion.p>

        {/* Badges */}
        <motion.div {...fadeUp} className="mt-5 flex flex-wrap items-center justify-center gap-2" data-id="element-10">
          {['📄 100% PDF', '⚡️ Instant Delivery', '📚 6 Volumes + 5 Bonuses'].map(b => <span key={b} className="rounded-full border border-orange-500/40 bg-neutral-900/70 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm" data-id="element-11">
              {b}
            </span>)}
        </motion.div>

        <motion.div {...fadeUp} className="mt-6" data-id="element-12">
          <CTAButton label="I Want to Improve My Practices" data-id="element-13" />
        </motion.div>

        {/* ===== PROBLEM ===== */}
        <motion.section {...fadeUp} className="mt-12" data-id="element-14">
          <div className="mb-4 flex items-center justify-center gap-2 text-center" data-id="element-15">
            <AlertTriangleIcon className="h-5 w-5 shrink-0 text-orange-400" data-id="element-16" />
            <h2 className="font-display text-2xl uppercase leading-tight text-white" data-id="element-17">
              Coaching basketball can be chaotic — and we get it
            </h2>
          </div>
          <ul className="space-y-2.5" data-id="element-18">
            {PROBLEMS.map((p, i) => <li key={i} className="flex items-start gap-3 rounded-xl border border-neutral-700 bg-neutral-900/70 p-3.5 text-sm text-neutral-200 backdrop-blur-sm" data-id="element-19">
                <span className="mt-0.5 shrink-0 text-lg" aria-hidden data-id="element-20">
                  ❌
                </span>
                <span data-id="element-21">{p}</span>
              </li>)}
          </ul>
        </motion.section>

        {/* ===== SOLUTION / MODULES ===== */}
        <motion.section {...fadeUp} className="mt-12" data-id="element-22">
          <h2 className="mb-1 text-center font-display text-2xl uppercase text-white" data-id="element-23">
            That is why we created{' '}
            <span className="text-orange-400" data-id="element-24">Basketball Resolved</span>
          </h2>
          <p className="mb-5 text-center text-sm text-neutral-300" data-id="element-25">
            6 practical volumes covering technique, injury prevention,
            performance, psychology, youth development, and safe return to play.
          </p>

          {MODULE_GROUPS.map(group => <div key={group.label} className="mb-5" data-id="element-26">
              <div className="mb-2.5 rounded-lg bg-orange-500/15 px-3 py-2 text-center text-xs font-extrabold uppercase tracking-wider text-orange-400" data-id="element-27">
                {group.label}
              </div>
              <div className="space-y-2.5" data-id="element-28">
                {MODULES.slice(group.range[0], group.range[1]).map(m => <Accordion key={m.title} emoji={m.emoji} title={m.title} body={m.body} data-id="element-29" />)}
              </div>
            </div>)}

          <div className="mt-6" data-id="element-30">
            <CTAButton label="I Want to Improve My Practices" data-id="element-31" />
          </div>
        </motion.section>

        {/* ===== REVIEWS (5 stars) ===== */}
        <motion.section {...fadeUp} className="mt-12" data-id="element-32">
          <h2 className="mb-4 text-center font-display text-xl uppercase text-white" data-id="element-33">
            Coaches Who Already Use It ⭐️⭐️⭐️⭐️⭐️
          </h2>
          <Reviews data-id="element-34" />
        </motion.section>

        {/* ===== BONUSES ===== */}
        <motion.section {...fadeUp} className="mt-12" data-id="element-35">
          <h2 className="mb-1 text-center font-display text-2xl uppercase text-white" data-id="element-36">
            Plus, everything you need to save your practice
          </h2>
          <p className="mb-5 text-center text-sm text-neutral-300" data-id="element-37">
            Get 5 player-ready bonuses for smarter training, safer performance,
            and mental confidence.
          </p>

          <BonusCarousel data-id="element-38" />
        </motion.section>

        {/* ===== COMPARISON ===== */}
        <motion.section {...fadeUp} className="mt-12" data-id="element-39">
          <p className="mb-2 text-center text-xs font-extrabold uppercase tracking-[0.18em] text-orange-400" data-id="element-40">
            +250 Pages
          </p>
          <h2 className="mb-4 text-center font-display text-xl uppercase text-white" data-id="element-41">
            Without Basketball Resolved{' '}
            <span className="text-orange-400" data-id="element-42">vs</span> With Basketball Resolved
          </h2>
          <ComparisonTable data-id="element-43" />
          <p className="mt-5 text-center font-display text-xl uppercase leading-tight text-white" data-id="element-44">
            Your athletes don’t need more workouts to improve.{' '}
            <span className="text-orange-400" data-id="element-45">They need a system.</span>
          </p>
        </motion.section>

        {/* ===== FINAL OFFER / PURCHASE ===== */}
        <motion.section {...fadeUp} className="mt-12" data-id="element-46">
          <PurchaseSection data-id="element-47" />
        </motion.section>

        {/* ===== OBJECTIONS + SUPPORT ===== */}
        <motion.section {...fadeUp} className="mt-12" data-id="element-48">
          <ObjectionsSupport data-id="element-49" />
        </motion.section>

        {/* ===== TESTIMONIALS ===== */}
        <motion.section {...fadeUp} className="mt-12" data-id="element-50">
          <h2 className="mb-4 text-center font-display text-xl uppercase text-white" data-id="element-51">
            What Coaches Are Saying
          </h2>
          <Testimonials data-id="element-52" />

          <div className="mt-6" data-id="element-53">
            <CTAButton label="I Want to Improve My Practices" data-id="element-54" />
          </div>
        </motion.section>

        {/* Footer */}
        <footer className="mt-12 text-center" data-id="element-55">
          <p className="font-display text-lg uppercase text-orange-400" data-id="element-56">
            Basketball Resolved
          </p>
          <p className="mt-1 text-xs text-neutral-500" data-id="element-57">
            © {new Date().getFullYear()} · All rights reserved
          </p>
        </footer>
      </main>
    </div>;
}